import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "wouter";
import {
  ArrowLeft,
  BadgeCheck,
  CalendarDays,
  Clock3,
  CircleAlert,
  FileImage,
  ImagePlus,
  Link2,
  LoaderCircle,
  LockKeyhole,
  LogIn,
  Save,
  ShieldAlert,
  UsersRound,
} from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { chiNotify } from "@/components/ChiNotification";
import { LINKEDIN_PROFILE_URL } from "@/lib/speakerProfileValidation";
import { trpc } from "@/lib/trpc";
import { LinkedInInput, linkedinUsernameFromStored, storedLinkedInValue } from "@/components/ProfileContactInputs";
import BiographyAssistant from "@/components/BiographyAssistant";
import { MAX_IMAGE_UPLOAD_BYTES } from "@shared/imageUploadLimits";

// Same ceiling as every other image upload, from one shared constant.
const MAX_FLYER_BYTES = MAX_IMAGE_UPLOAD_BYTES;
const FORBIDDEN_TEXT_CHARACTERS = /[<>'"]/g;

type SpeakerPortalField = "bio" | "linkedinUrl";
type SpeakerPortalFieldErrors = Partial<Record<SpeakerPortalField, string>>;

const panelStyle: React.CSSProperties = {
  background: "linear-gradient(145deg, rgba(18,25,44,0.96), rgba(10,15,28,0.96))",
  border: "1px solid rgba(255,255,255,0.09)",
  borderRadius: "1.1rem",
  boxShadow: "0 18px 48px rgba(0,0,0,0.2)",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  border: "1px solid rgba(255,255,255,0.13)",
  borderRadius: "0.65rem",
  background: "rgba(255,255,255,0.045)",
  color: "#F8FAFC",
  fontFamily: "'Inter', sans-serif",
  fontSize: "0.9rem",
  outline: "none",
};

const assignedTalkPillStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  boxSizing: "border-box",
  width: "fit-content",
  maxWidth: "100%",
  margin: "0.55rem 0 0",
  padding: "0.42rem 0.74rem",
  border: "1px solid rgba(186,231,255,0.28)",
  borderRadius: "999px",
  color: "#F4F9FF",
  background: "linear-gradient(135deg, rgba(0,212,255,0.19), rgba(168,85,247,0.18) 48%, rgba(233,30,140,0.18))",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 8px 20px rgba(0,0,0,0.16)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: "0.88rem",
  fontWeight: 750,
  lineHeight: 1.35,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
};

function sanitizeProfileText(value: string) {
  return value.replace(FORBIDDEN_TEXT_CHARACTERS, "").trim();
}

function PortalFrame({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ minHeight: "100vh", background: "#080C18" }}>
      <Navigation />
      <main style={{ padding: "clamp(7.5rem, 13vw, 9.5rem) 0 5rem" }}>{children}</main>
      <Footer />
    </div>
  );
}

function AccessState({
  icon,
  eyebrow,
  title,
  children,
  accent = "#E91E8C",
}: {
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  children: React.ReactNode;
  accent?: string;
}) {
  return (
    <PortalFrame>
      <div className="container" style={{ maxWidth: 780 }}>
        <section style={{ ...panelStyle, padding: "clamp(2rem, 6vw, 4.5rem)", textAlign: "center", position: "relative", overflow: "hidden" }}>
          <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 0%, ${accent}1C, transparent 50%)`, pointerEvents: "none" }} />
          <div style={{ position: "relative" }}>
            <div style={{ width: 68, height: 68, display: "grid", placeItems: "center", margin: "0 auto 1.25rem", borderRadius: "1.15rem", color: accent, background: `${accent}18`, border: `1px solid ${accent}42` }}>
              {icon}
            </div>
            <p style={{ margin: "0 0 0.65rem", color: accent, fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.71rem", fontWeight: 750, letterSpacing: "0.12em", textTransform: "uppercase" }}>{eyebrow}</p>
            <h1 style={{ margin: 0, color: "#FFFFFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2rem, 5vw, 3.2rem)", letterSpacing: "0.025em", lineHeight: 1.05 }}>{title}</h1>
            <div style={{ maxWidth: 525, margin: "1.1rem auto 0", color: "#A8B3C7", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.75 }}>{children}</div>
          </div>
        </section>
      </div>
    </PortalFrame>
  );
}

export default function SpeakerPortal() {
  const utils = trpc.useUtils();
  const accessQuery = trpc.speakers.access.useQuery(undefined, { retry: false });
  const isApproved = accessQuery.data?.state === "approved";
  const portalQuery = trpc.speakers.portal.useQuery(undefined, {
    enabled: isApproved,
    retry: false,
  });
  const [bio, setBio] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [profileFieldErrors, setProfileFieldErrors] = useState<SpeakerPortalFieldErrors>({});
  const [profileError, setProfileError] = useState<string | null>(null);
  const [uploadingEventSlug, setUploadingEventSlug] = useState<string | null>(null);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const updateOwnProfile = trpc.speakers.updateOwnProfile.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.speakers.portal.invalidate(),
        utils.speakers.access.invalidate(),
      ]);
      setProfileFieldErrors({});
      setProfileError(null);
      chiNotify.success("Profile saved", { description: "Your professional bio and LinkedIn URL are up to date." });
    },
    onError: error => {
      setProfileError(error.message || "We could not save your profile. Please review the highlighted fields.");
      chiNotify.error("Profile not saved", { description: error.message || "Please try again." });
    },
  });

  const uploadPromotionalGraphic = trpc.speakers.uploadPromotionalGraphic.useMutation({
    onSuccess: async () => {
      await utils.speakers.portal.invalidate();
      chiNotify.success("Promotional graphic saved", { description: "The graphic is now attached to this assigned talk." });
    },
    onError: error => {
      chiNotify.error("Graphic not uploaded", { description: error.message || "Please use an approved flyer size and try again." });
    },
    onSettled: () => setUploadingEventSlug(null),
  });

  const portal = portalQuery.data;
  const profile = portal?.profile;

  useEffect(() => {
    if (!profile) return;
    setBio(profile.bio);
    setLinkedinUrl(linkedinUsernameFromStored(profile.linkedinUrl));
  }, [profile?.id, profile?.bio, profile?.linkedinUrl]);

  const normalizedBio = useMemo(() => sanitizeProfileText(bio), [bio]);
  /*
   * The field holds a username; the API expects the profile URL. Conversion here
   * rather than in the submit handler so the validation below tests the value
   * that will actually be sent.
   */
  const normalizedLinkedinUrl = useMemo(() => {
    try {
      return storedLinkedInValue(linkedinUrl) ?? "";
    } catch {
      return "";
    }
  }, [linkedinUrl]);
  const textWasSanitized = bio !== normalizedBio;
  const linkedinError = profileFieldErrors.linkedinUrl;
  const bioError = profileFieldErrors.bio;

  function clearProfileFieldError(field: SpeakerPortalField) {
    setProfileFieldErrors(current => {
      if (!current[field]) return current;
      const { [field]: _cleared, ...remaining } = current;
      return remaining;
    });
  }

  function saveProfile() {
    const nextErrors: SpeakerPortalFieldErrors = {};
    if (!LINKEDIN_PROFILE_URL.test(normalizedLinkedinUrl)) {
      nextErrors.linkedinUrl = "Use a public LinkedIn URL in the https://www.linkedin.com/in/{profile} format.";
    }
    if (normalizedBio.length < 40) {
      nextErrors.bio = "Provide at least 40 characters in your professional biography.";
    }

    const firstInvalidField: SpeakerPortalField | undefined = nextErrors.linkedinUrl
      ? "linkedinUrl"
      : nextErrors.bio
        ? "bio"
        : undefined;
    if (firstInvalidField) {
      setProfileFieldErrors(nextErrors);
      setProfileError("Review the highlighted fields and correct the information before saving your profile.");
      requestAnimationFrame(() => document.getElementById(`speaker-portal-${firstInvalidField}`)?.focus());
      return;
    }

    setProfileFieldErrors({});
    setProfileError(null);
    updateOwnProfile.mutate({ bio: normalizedBio, linkedinUrl: normalizedLinkedinUrl });
  }

  function selectPromotionalGraphic(eventSlug: string) {
    fileInputRefs.current[eventSlug]?.click();
  }

  function uploadFile(eventSlug: string, file?: File) {
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      chiNotify.error("Unsupported file", { description: "Upload a PNG or JPEG promotional graphic." });
      return;
    }
    if (file.size > MAX_FLYER_BYTES) {
      chiNotify.error("Graphic is too large", { description: "Promotional graphics must be 10 MB or smaller." });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        chiNotify.error("Graphic could not be read", { description: "Please select a PNG or JPEG file and try again." });
        return;
      }
      setUploadingEventSlug(eventSlug);
      uploadPromotionalGraphic.mutate({ eventSlug, dataUrl: reader.result });
    };
    reader.onerror = () => chiNotify.error("Graphic could not be read", { description: "Please select the file again." });
    reader.readAsDataURL(file);
  }

  if (accessQuery.isLoading) {
    return (
      <PortalFrame>
        <div className="container" style={{ maxWidth: 780 }}>
          <section style={{ ...panelStyle, padding: "4rem 2rem", display: "grid", placeItems: "center", color: "#94A3B8", gap: "1rem" }} aria-live="polite">
            <LoaderCircle className="chi-notification-spinner" size={26} />
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.88rem" }}>Checking secure speaker access…</span>
          </section>
        </div>
      </PortalFrame>
    );
  }

  if (accessQuery.isError) {
    return (
      <AccessState icon={<LogIn size={30} />} eyebrow="Secure access" title="Sign in to continue" accent="#00D4FF">
        <p style={{ margin: "0 0 1.5rem" }}>Your Speaker Workspace is available after a secure account sign-in. Use the same email address connected to your speaker profile.</p>
        <Link href="/login" className="chi-btn-primary" style={{ display: "inline-flex", padding: "0.8rem 1.25rem", textDecoration: "none" }}>Sign in securely</Link>
      </AccessState>
    );
  }

  if (accessQuery.data?.state === "not_speaker") {
    return (
      <AccessState icon={<CircleAlert size={30} />} eyebrow="Speaker Workspace" title="Speaker access is not available yet" accent="#00D4FF">
        <p style={{ margin: "0 0 1.5rem" }}>This signed-in account is not associated with an approved speaker profile. A Chi Alliance administrator can grant Speaker Workspace access after approving a linked speaker profile; your membership and billing status remain separate.</p>
        <Link href="/collaborate-with-us" className="chi-btn-primary" style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", padding: "0.8rem 1.25rem", textDecoration: "none" }}>
          Explore speaking opportunities <ArrowLeft size={15} style={{ transform: "rotate(180deg)" }} />
        </Link>
      </AccessState>
    );
  }

  if (accessQuery.data?.state === "pending") {
    return (
      <AccessState icon={<Clock3 size={30} />} eyebrow="Application received" title="Your Speaker Workspace is under review" accent="#F5A623">
        <p style={{ margin: 0 }}>A Chi Alliance administrator is reviewing your speaker application. Speaker Workspace access, assigned engagements, and profile editing become available only after approval. We will contact you at the email submitted with your application when there is an update.</p>
      </AccessState>
    );
  }

  if (accessQuery.data?.state === "suspended") {
    return (
      <AccessState icon={<ShieldAlert size={30} />} eyebrow="Access unavailable" title="Speaker Workspace access is paused" accent="#FB7185">
        <p style={{ margin: 0 }}>This speaker profile is not currently active. For help with access, please contact The Chi Alliance team through the standard community channels.</p>
      </AccessState>
    );
  }

  if (portalQuery.isLoading || !profile) {
    return (
      <PortalFrame>
        <div className="container" style={{ maxWidth: 1080 }}>
          <section style={{ ...panelStyle, padding: "4rem 2rem", display: "grid", placeItems: "center", color: "#94A3B8", gap: "1rem" }} aria-live="polite">
            <LoaderCircle className="chi-notification-spinner" size={26} />
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.88rem" }}>Loading your speaker workspace…</span>
          </section>
        </div>
      </PortalFrame>
    );
  }

  if (portalQuery.isError) {
    return (
      <AccessState icon={<CircleAlert size={30} />} eyebrow="Speaker Workspace" title="We could not load your workspace" accent="#FB7185">
        <p style={{ margin: "0 0 1.5rem" }}>Please refresh the page. If this continues, contact The Chi Alliance team for access support.</p>
        <button type="button" onClick={() => portalQuery.refetch()} className="chi-btn-primary" style={{ padding: "0.8rem 1.25rem" }}>Try again</button>
      </AccessState>
    );
  }

  return (
    <PortalFrame>
      <div className="container" style={{ maxWidth: 1180 }}>
        <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: "1.5rem", flexWrap: "wrap", marginBottom: "2rem" }}>
          <div>
            <p style={{ margin: "0 0 0.45rem", color: "#F9A8D4", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.72rem", fontWeight: 750, letterSpacing: "0.12em", textTransform: "uppercase" }}>The Chi Alliance · Speaker workspace</p>
            <h1 style={{ margin: 0, color: "#FFFFFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(2.35rem, 5vw, 4rem)", lineHeight: 0.98, letterSpacing: "0.02em" }}>Welcome, {profile.fullName}</h1>
            <p style={{ margin: "0.85rem 0 0", maxWidth: 650, color: "#A8B3C7", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.7 }}>Manage the public professional details you have authorized for your speaker profile and review the upcoming engagements assigned to you. Event details and attendance figures remain read-only.</p>
          </div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", padding: "0.55rem 0.75rem", borderRadius: "999px", color: "#86EFAC", border: "1px solid rgba(74,222,128,0.34)", background: "rgba(74,222,128,0.09)", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.75rem", fontWeight: 700 }}>
            <BadgeCheck size={15} /> Approved speaker
          </div>
        </header>

        <div className="speaker-portal-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.15fr) minmax(320px, 0.85fr)", gap: "1.25rem", alignItems: "start" }}>
          <section style={{ ...panelStyle, padding: "clamp(1.25rem, 3vw, 2rem)" }} aria-labelledby="assigned-talks-heading">
            <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "center", marginBottom: "1.25rem" }}>
              <div>
                <p style={{ margin: "0 0 0.28rem", color: "#E91E8C", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.67rem", fontWeight: 750, letterSpacing: "0.12em", textTransform: "uppercase" }}>Read-only program details</p>
                <h2 id="assigned-talks-heading" style={{ margin: 0, color: "#FFFFFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.75rem", letterSpacing: "0.035em" }}>Upcoming engagements</h2>
              </div>
              <span style={{ color: "#8E9AAF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.77rem", fontWeight: 650 }}>{portal.engagements.length} {portal.engagements.length === 1 ? "engagement" : "engagements"}</span>
            </div>

            {portal.engagements.length === 0 ? (
              <div style={{ padding: "1.8rem", borderRadius: "0.8rem", border: "1px dashed rgba(255,255,255,0.16)", color: "#94A3B8", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>Your speaker access is active, but no upcoming engagements are assigned yet. The Chi Alliance team will add confirmed event details here.</div>
            ) : (
              <div style={{ display: "grid", gap: "0.9rem" }}>
                {portal.engagements.map(assignment => {
                  const event = assignment.event;
                  const isUploading = uploadingEventSlug === assignment.eventSlug && uploadPromotionalGraphic.isPending;
                  return (
                    <article key={assignment.id} style={{ padding: "1rem", borderRadius: "0.85rem", border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.025)" }}>
                      <div data-assigned-talk-summary="true" style={{ display: "flex", justifyContent: "space-between", gap: "1rem", alignItems: "flex-start", flexWrap: "wrap" }}>
                        <div style={{ flex: "1 1 18rem", minWidth: 0, maxWidth: "100%" }}>
                          <div data-assigned-talk-date="true" style={{ display: "inline-flex", alignItems: "center", gap: "0.38rem", color: event?.color ?? "#F5A623", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.68rem", fontWeight: 750, letterSpacing: "0.09em", textTransform: "uppercase" }}>
                            <CalendarDays size={13} /> {event.displayDate}
                          </div>
                          <h3 data-assigned-talk-pill="true" style={assignedTalkPillStyle}>{event.title}</h3>
                          <p style={{ margin: "0.35rem 0 0", color: "#8793A7", fontFamily: "'Inter', sans-serif", fontSize: "0.82rem", overflowWrap: "anywhere" }}>{event.time} · {event.location}</p>
                        </div>
                        <div data-assigned-talk-registration="true" style={{ display: "inline-flex", flex: "0 0 auto", alignItems: "center", gap: "0.42rem", color: "#A8B3C7", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.75rem", whiteSpace: "nowrap" }}>
                          <UsersRound size={14} color="#00D4FF" /> {event.registered} registered
                        </div>
                      </div>

                      <div style={{ marginTop: "0.95rem", paddingTop: "0.9rem", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", gap: "0.75rem", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: assignment.promotionalGraphicUrl ? "#86EFAC" : "#8E9AAF", fontFamily: "'Inter', sans-serif", fontSize: "0.78rem" }}>
                          <FileImage size={15} />
                          {assignment.promotionalGraphicUrl ? `Graphic attached · ${assignment.promotionalGraphicWidth}×${assignment.promotionalGraphicHeight}` : "No promotional graphic uploaded"}
                        </div>
                        <input
                          ref={node => { fileInputRefs.current[assignment.eventSlug] = node; }}
                          type="file"
                          accept="image/png,image/jpeg"
                          style={{ display: "none" }}
                          onChange={eventChange => {
                            uploadFile(assignment.eventSlug, eventChange.currentTarget.files?.[0]);
                            eventChange.currentTarget.value = "";
                          }}
                        />
                        <button type="button" onClick={() => selectPromotionalGraphic(assignment.eventSlug)} disabled={uploadPromotionalGraphic.isPending} style={{ minHeight: 38, display: "inline-flex", alignItems: "center", gap: "0.42rem", padding: "0.55rem 0.75rem", borderRadius: "0.55rem", cursor: uploadPromotionalGraphic.isPending ? "wait" : "pointer", color: "#F9A8D4", background: "rgba(233,30,140,0.10)", border: "1px solid rgba(233,30,140,0.32)", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.74rem", fontWeight: 700, opacity: uploadPromotionalGraphic.isPending && !isUploading ? 0.48 : 1 }}>
                          {isUploading ? <LoaderCircle className="chi-notification-spinner" size={14} /> : <ImagePlus size={14} />}
                          {isUploading ? "Uploading…" : assignment.promotionalGraphicUrl ? "Replace graphic" : "Upload graphic"}
                        </button>
                      </div>
                      <p style={{ margin: "0.75rem 0 0", color: "#657289", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.5 }}>PNG or JPEG only. Accepted dimensions: 2550×3300 (US Letter), 2480×3508 (A4), or 1080×1350 (social portrait). Maximum 10 MB.</p>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <aside style={{ ...panelStyle, padding: "clamp(1.25rem, 3vw, 2rem)" }} aria-labelledby="profile-editor-heading">
            <p style={{ margin: "0 0 0.28rem", color: "#00D4FF", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.67rem", fontWeight: 750, letterSpacing: "0.12em", textTransform: "uppercase" }}>Your authorized profile details</p>
            <h2 id="profile-editor-heading" style={{ margin: 0, color: "#FFFFFF", fontFamily: "'Bebas Neue', sans-serif", fontSize: "1.75rem", letterSpacing: "0.035em" }}>Professional profile</h2>
            <p style={{ margin: "0.65rem 0 1.25rem", color: "#8E9AAF", fontFamily: "'Inter', sans-serif", fontSize: "var(--chi-body-compact)", lineHeight: 1.6 }}>You may update only your biography and LinkedIn profile. Event details and attendance data remain read-only.</p>

            <div style={{ display: "grid", gap: "1rem" }}>
              <div>
                <label htmlFor="speaker-portal-linkedin" style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginBottom: "0.45rem", color: linkedinError ? "#FDA4AF" : "#CDD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.75rem", fontWeight: 700 }}><Link2 size={13} color="#00D4FF" /> LinkedIn URL <span aria-hidden="true" style={{ color: "#F9A8D4" }}>*</span></label>
                {/*
                  The shared control: a username after a fixed prefix, stored as
                  the full profile URL. This was a free-text URL field validated
                  after the fact, so the most common way to get it wrong — pasting
                  a company page, or a URL with tracking parameters — was only
                  caught on submit.
                */}
                <LinkedInInput
                  id="speaker-portal-linkedin"
                  required
                  value={linkedinUsernameFromStored(linkedinUrl)}
                  onChange={username => { setLinkedinUrl(username); clearProfileFieldError("linkedinUrl"); setProfileError(null); }}
                />
                <p id="speaker-portal-linkedin-help" style={{ margin: "0.4rem 0 0", color: "#657289", fontFamily: "'Inter', sans-serif", fontSize: "0.7rem", lineHeight: 1.45 }}>Use the complete public https://www.linkedin.com/in/{"{profile}"} URL.</p>
                {linkedinError && <p id="speaker-portal-linkedin-error" role="alert" style={{ margin: "0.35rem 0 0", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", lineHeight: 1.45 }}>{linkedinError}</p>}
              </div>
              <div>
                <label htmlFor="speaker-portal-bio" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", marginBottom: "0.45rem", color: bioError ? "#FDA4AF" : "#CDD5E1", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.75rem", fontWeight: 700 }}><span>Professional biography <span aria-hidden="true" style={{ color: "#F9A8D4" }}>*</span></span><span style={{ color: "#657289", fontSize: "0.67rem", fontWeight: 600 }}>{bio.length}/8000</span></label>
                <textarea id="speaker-portal-bio" required minLength={40} maxLength={8000} rows={10} aria-invalid={Boolean(bioError)} aria-describedby={bioError ? "speaker-portal-bio-help speaker-portal-bio-error" : "speaker-portal-bio-help"} value={bio} onChange={event => { setBio(event.target.value.replace(FORBIDDEN_TEXT_CHARACTERS, "")); clearProfileFieldError("bio"); setProfileError(null); }} onFocus={event => { event.currentTarget.style.borderColor = "rgba(0,212,255,.62)"; }} onBlur={event => { event.currentTarget.style.borderColor = bioError ? "rgba(251,113,133,.75)" : "rgba(255,255,255,.13)"; }} style={{ ...inputStyle, minHeight: 210, padding: "0.75rem 0.85rem", resize: "vertical", lineHeight: 1.6, borderColor: bioError ? "rgba(251,113,133,.75)" : "rgba(255,255,255,.13)" }} />
                <BiographyAssistant value={bio} maxLength={8000} onApprove={revised => { setBio(revised); clearProfileFieldError("bio"); setProfileError(null); }} />
                <p id="speaker-portal-bio-help" style={{ margin: "0.4rem 0 0", color: "#657289", fontFamily: "'Inter', sans-serif", fontSize: "0.7rem", lineHeight: 1.45 }}>At least 40 characters. Markup characters are removed before the profile is saved.</p>
                {bioError && <p id="speaker-portal-bio-error" role="alert" style={{ margin: "0.35rem 0 0", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", lineHeight: 1.45 }}>{bioError}</p>}
              </div>
              {textWasSanitized && <p role="status" style={{ margin: 0, color: "#F5A623", fontFamily: "'Inter', sans-serif", fontSize: "0.75rem", lineHeight: 1.45 }}>Unsupported markup characters were removed from your biography for account safety.</p>}
              {profileError && <p role="alert" style={{ margin: 0, padding: "0.7rem 0.8rem", borderRadius: "0.6rem", border: "1px solid rgba(251,113,133,.35)", background: "rgba(251,113,133,.09)", color: "#FDA4AF", fontFamily: "'Inter', sans-serif", fontSize: "0.78rem", lineHeight: 1.45 }}>{profileError}</p>}
              <button type="button" onClick={saveProfile} disabled={updateOwnProfile.isPending} style={{ minHeight: 44, display: "inline-flex", justifyContent: "center", alignItems: "center", gap: "0.5rem", border: "1px solid rgba(0,212,255,.45)", borderRadius: "0.65rem", color: "#07101C", background: "linear-gradient(135deg,#00D4FF,#77E6FF)", fontFamily: "'Space Grotesk', sans-serif", fontSize: "0.82rem", fontWeight: 800, cursor: updateOwnProfile.isPending ? "wait" : "pointer", opacity: updateOwnProfile.isPending ? 0.7 : 1 }}>
                {updateOwnProfile.isPending ? <LoaderCircle className="chi-notification-spinner" size={15} /> : <Save size={15} />} {updateOwnProfile.isPending ? "Saving…" : "Save profile"}
              </button>
            </div>
          </aside>
        </div>
      </div>
      <style>{`@media (max-width: 860px) { .speaker-portal-grid { grid-template-columns: minmax(0, 1fr) !important; } }`}</style>
    </PortalFrame>
  );
}
